const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { default: Typography } = require('@mui/material/Typography');
const { NavLink: Link } = require('react-router-dom');

const BottomButton = require('components/BottomButton');
const { analyticsTemplates } = require('utils/analytics');
const useForm = require('hooks/useForm');
const LoginHelpFormConfig = require('./formConfig');

const internals = {};

const FORM_ID = 'login-help-form';

const isTeamsApp = process.env.APP_FLAVOUR === 'teams';

module.exports = function LoginHelpContents(props) {

    const {
        loginContext = {},
        onSubmitBugReport,
        schools = [],
        signupContext = {}
    } = props;

    React.useEffect(() => {

        window.scrollTo(0, 0);
    }, []);

    const filteredSchools = React.useMemo(() => {

        return schools.filter((school) => {

            return isTeamsApp ? school.isCompany : !school.isCompany;
        });
    }, [schools]);

    const defaults = React.useMemo(() => LoginHelpFormConfig.buildDefaults({
        loginContext,
        schools: filteredSchools,
        signupContext
    }), [filteredSchools, loginContext, signupContext]);

    const submit = React.useCallback((values) => {

        return onSubmitBugReport(LoginHelpFormConfig.buildSubmitPayload({
            schools: filteredSchools,
            values
        }));
    }, [filteredSchools, onSubmitBugReport]);

    const formConfig = React.useMemo(() => LoginHelpFormConfig.buildFormConfig({
        onSubmit: submit,
        defaults,
        formId: FORM_ID,
        isTeamsApp,
        schools: filteredSchools
    }), [defaults, filteredSchools, submit]);

    const {
        methods,
        Form
    } = useForm(formConfig);

    const { isSubmitting } = methods.formState;

    const onSubmitClick = React.useCallback(() => {

        analyticsTemplates.buttons('submit support', 'support: submit');
    }, []);

    const {
        MainContainer,
        ContentContainer,
        InnerWrapper,
        FormWrapper,
        BottomButtonContainer
    } = internals;

    return <MainContainer>
        <ContentContainer>
            <InnerWrapper>
                <Typography
                    variant='h2'
                    style={{
                        marginTop: 25,
                        marginBottom: 20
                    }}
                >
                    Get Help Logging In
                </Typography>
                <Typography color='error' style={{ fontWeight: 'bold' }}>
                    Please make sure you are using the same email address you originally created your account with.
                </Typography>
                <ul style={{
                    paddingLeft: 20,
                    marginTop: 12
                }}>
                    <li>
                        <Typography>
                            If you have not yet received an email with a verification or reset link within 10 minutes, please check your Spam folder.
                        </Typography>
                    </li>
                    <li>
                        <Typography>
                            If you&apos;re not sure what your password is, you can reset it <Link to={`/forgot-password`}>here</Link>.
                        </Typography>
                    </li>
                    <li>
                        <Typography>
                            If you are still having trouble, please complete and submit the form below:
                        </Typography>
                    </li>
                </ul>
                <FormWrapper>
                    {Form}
                </FormWrapper>
                <div style={{ textAlign: 'center', marginTop: '15px' }}>
                    <Link to="/" style={{ textDecoration: 'underline', color: 'blue', marginRight: '15px' }}>Back to Login</Link>
                    <Link to="/signup" style={{ textDecoration: 'underline', color: 'blue' }}>Create Account</Link>
                </div>
            </InnerWrapper>
        </ContentContainer>
        <BottomButtonContainer>
            <BottomButton
                label='Submit'
                type='submit'
                form={FORM_ID}
                onClick={onSubmitClick}
                disabled={isSubmitting}
            />
        </BottomButtonContainer>
    </MainContainer>;
};

module.exports.propTypes = {
    loginContext: T.object,
    onSubmitBugReport: T.func.isRequired,
    schools: T.array,
    signupContext: T.object
};

internals.MainContainer = Styled.div`
    display: flex;
    flex-flow: column nowrap;
    width: 100%;
    margin: auto;
    max-width: 600px;

    header {
        top: var(--safezonetop);
    }
`;

internals.ContentContainer = Styled.div`
    flex-grow: 1;
    padding-bottom: 110px;
`;

internals.InnerWrapper = Styled.div`
    width: 100%;
    padding: 1.5em;
    display: flex;
    flex-flow: column nowrap;

    & > div {
        width: 100% !important;
    }

    h2 {
        margin-top: 0;
    }
`;

internals.FormWrapper = Styled.div`
    margin-top: 12px;
`;

internals.BottomButtonContainer = Styled.div`
    z-index: 100;
    position: fixed;
    bottom: 0;
    width: 100%;
    left: 0;
`;

module.exports.internals = internals;
